/**
 * Image banner: scale / exit tied to scroll position (Lenis- and layout-safe).
 */
(function () {
  var prefersReduced =
    typeof matchMedia === "function" &&
    matchMedia("(prefers-reduced-motion: reduce)").matches;

  function getScroller() {
    return document.querySelector("#smooth-wrapper");
  }

  /**
   * Scroll scale must follow the same scroll value that actually moves the page.
   * Banners in hero-group are outside #smooth-wrapper; using only wrapper.scrollTop
   * misses window/Lenis scroll and can delay or freeze the effect.
   */
  function getScrollerForRoot(root) {
    if (root && root.closest && root.closest("#smooth-wrapper")) {
      return getScroller();
    }
    return null;
  }

  function getActiveScrollerForRoot(root) {
    var rawScroller = getScrollerForRoot(root);
    if (rawScroller && window.lenis && typeof window.lenis.scroll === "number") {
      return rawScroller;
    }
    return null;
  }

  /**
   * Single source for vertical "page" scroll. Lenis does not always update
   * element.scrollTop; use lenis.scroll when the smoother is active.
   */
  function getScrollYDocument() {
    if (window.lenis && typeof window.lenis.scroll === "number") {
      return window.lenis.scroll;
    }
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function getScrollTop(scroller) {
    if (window.lenis && typeof window.lenis.scroll === "number") {
      return window.lenis.scroll;
    }
    if (scroller) {
      var canScroll = scroller.scrollHeight > scroller.clientHeight;
      if (canScroll) return scroller.scrollTop;
    }
    return getScrollYDocument();
  }

  function getViewportHeight(scroller) {
    if (scroller) return scroller.clientHeight;
    return window.innerHeight;
  }

  function parseScaleMax(root) {
    var raw = root.getAttribute("data-scale-max");
    var n = parseFloat(raw, 10);
    if (!isFinite(n) || n < 1) return 1.3;
    return Math.min(n, 5);
  }

  /**
   * Hero exit: the whole effect (scale up + exit) scrubs over this scroll distance,
   * so at typical scroll rates it feels like ~1s or less. Tuned in viewport height.
   */
  var EFFECT_SCROLL_RANGE_VH = 0.5;
  var EFFECT_SCROLL_RANGE_PX = { min: 280, max: 800 };
  /** Vertical exit as a fraction of viewport (translateY up). */
  var EFFECT_EXIT_VH = 0.3;
  /** Opacity: 1 from p=0..this, then fade to 0 by p=1. */
  var OPACITY_FADE_START = 0.65;

  function getEffectRangePx(scroller) {
    var vh = getViewportHeight(scroller);
    var n = Math.round(EFFECT_SCROLL_RANGE_VH * vh);
    if (n < EFFECT_SCROLL_RANGE_PX.min) n = EFFECT_SCROLL_RANGE_PX.min;
    if (n > EFFECT_SCROLL_RANGE_PX.max) n = EFFECT_SCROLL_RANGE_PX.max;
    return n;
  }

  /**
   * Baseline scroll for p=0: current scroll the moment we measure, so the
   * effect tracks every pixel of movement from the first scroll, not
   * ScrollTrigger "top top" / section-alignment (which felt like scaling only
   * when the hero was leaving the viewport, especially with sticky/fixed heroes).
   */
  function getAlignScrollStart(_root, scroller) {
    return getScrollTop(scroller);
  }

  /**
   * Theme editor / dynamic sections dispatch shopify:section:load with the
   * `.shopify-section` host as `event.target`. Infinite scroll and other code
   * sometimes dispatch the same event on `document`, which would make
   * `target.querySelector(...)` search the whole page and re-init unrelated
   * (e.g. hidden) image banners.
   */
  function getShopifySectionRootFromEventTarget(t) {
    if (!t || t.nodeType !== 1) return null;
    if (t.id && t.id.indexOf("shopify-section-") === 0) return t;
    if (t.classList && t.classList.contains("shopify-section")) return t;
    if (t.closest) return t.closest(".shopify-section");
    return null;
  }

  function destroyAlignForSection(root) {
    if (!root) return;
    if (typeof root._imageBannerAlignDestroy === "function") {
      root._imageBannerAlignDestroy();
      root._imageBannerAlignDestroy = null;
    }
  }

  /** Fix-position bar matches #MainContent horizontal box (sidebars / grid). */
  function initAlignToMainContent(root) {
    if (root.getAttribute("data-sticky-active") !== "true") {
      return;
    }
    var fixed = root.querySelector(".simple-image-banner__fixed");
    if (!fixed) {
      return;
    }
    var main = document.getElementById("MainContent");
    destroyAlignForSection(root);
    if (!main) {
      fixed.style.left = "0px";
      fixed.style.width = "100%";
      fixed.classList.remove("simple-image-banner__fixed--pending");
      return;
    }

    function sync() {
      var r = main.getBoundingClientRect();
      fixed.style.left = r.left + "px";
      fixed.style.width = r.width + "px";
    }

    root._imageBannerMainSync = sync;

    function clearFallback() {
      if (root._imageBannerAlignFallbackId != null) {
        clearTimeout(root._imageBannerAlignFallbackId);
        root._imageBannerAlignFallbackId = null;
      }
    }

    function reveal() {
      clearFallback();
      fixed.classList.remove("simple-image-banner__fixed--pending");
    }

    function showWhenStable() {
      sync();
      if (prefersReduced) {
        requestAnimationFrame(function () {
          sync();
          reveal();
        });
        return;
      }
      requestAnimationFrame(function () {
        sync();
        requestAnimationFrame(function () {
          sync();
          reveal();
        });
      });
    }

    root._imageBannerAlignFallbackId = setTimeout(function () {
      if (!fixed.classList.contains("simple-image-banner__fixed--pending")) {
        return;
      }
      sync();
      reveal();
    }, 1500);

    showWhenStable();

    window.addEventListener("resize", sync, { passive: true });
    var ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(function () {
        sync();
      });
      ro.observe(main);
    }

    root._imageBannerAlignDestroy = function () {
      window.removeEventListener("resize", sync);
      if (ro) ro.disconnect();
      fixed.style.left = "";
      fixed.style.width = "";
      clearFallback();
      root._imageBannerMainSync = null;
    };
  }

  function initNative(root, img, maxScale) {
    var scroller = getActiveScrollerForRoot(root);
    var resizeHandler;
    var scrollHandler;
    var nativeAlive = { ok: true };
    var lenisScrollProxy;

    function clearMeasure() {
      root._ibAlignStart = null;
      root._ibRangePx = null;
    }

    function getStart() {
      if (root._ibAlignStart == null) {
        root._ibAlignStart = getAlignScrollStart(root, scroller);
      }
      return root._ibAlignStart;
    }

    function getRange() {
      if (root._ibRangePx == null) {
        root._ibRangePx = getEffectRangePx(scroller);
      }
      return root._ibRangePx;
    }

    function applyTransform(p) {
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      var vh = getViewportHeight(scroller);
      var s = 1 + (maxScale - 1) * p;
      var ty = -p * EFFECT_EXIT_VH * vh;
      var op = 1;
      if (p > OPACITY_FADE_START) {
        op = 1 - (p - OPACITY_FADE_START) / (1 - OPACITY_FADE_START);
      }
      img.style.transform =
        "translate3d(0, " + ty + "px, 0) scale(" + s + ")";
      img.style.transformOrigin = "50% 45%";
      img.style.opacity = String(op);
    }

    function onScroll() {
      var st = getScrollTop(scroller);
      var p = (st - getStart()) / getRange();
      applyTransform(p);
    }

    scrollHandler = onScroll;
    var scrollTarget = scroller || window;
    scrollTarget.addEventListener("scroll", scrollHandler, { passive: true });
    if (window.lenis && typeof window.lenis.on === "function") {
      lenisScrollProxy = function () {
        if (nativeAlive.ok) onScroll();
      };
      window.lenis.on("scroll", lenisScrollProxy);
    }

    resizeHandler = function () {
      clearMeasure();
      onScroll();
    };
    window.addEventListener("resize", resizeHandler);

    img.style.transition = "none";
    onScroll();

    return function destroy() {
      nativeAlive.ok = false;
      scrollTarget.removeEventListener("scroll", scrollHandler);
      if (lenisScrollProxy && window.lenis) {
        if (typeof window.lenis.off === "function") {
          window.lenis.off("scroll", lenisScrollProxy);
        }
      }
      window.removeEventListener("resize", resizeHandler);
      clearMeasure();
      img.style.transform = "";
      img.style.transformOrigin = "";
      img.style.opacity = "";
      img.style.transition = "";
    };
  }

  function initGsap(root, img, maxScale) {
    var scrollerEl = getActiveScrollerForRoot(root);
    var triggerRoot = root.closest(".shopify-section") || root;
    var sid = root.getAttribute("data-section-id") || "default";

    try {
      gsap.registerPlugin(ScrollTrigger);
    } catch (e) {
      /* ignore */
    }

    var stOpts = {
      trigger: triggerRoot,
      start: "top top",
      end: function () {
        return "+=" + getEffectRangePx(scrollerEl);
      },
      scrub: 0.15,
      scroller: scrollerEl || window,
      invalidateOnRefresh: true,
      id: "image-banner-scale-" + sid,
      onUpdate: function (self) {
        var p = self.progress;
        if (p < 0) p = 0;
        if (p > 1) p = 1;
        var op = 1;
        if (p > OPACITY_FADE_START) {
          op = 1 - (p - OPACITY_FADE_START) / (1 - OPACITY_FADE_START);
        }
        img.style.opacity = String(op);
      }
    };

    var yEnd =
      -Math.round(
        EFFECT_EXIT_VH *
          (scrollerEl && scrollerEl.clientHeight
            ? scrollerEl.clientHeight
            : window.innerHeight)
      );

    img.style.transition = "none";

    var tween = gsap.fromTo(
      img,
      { scale: 1, y: 0, transformOrigin: "50% 45%" },
      {
        scale: maxScale,
        y: yEnd,
        ease: "none",
        scrollTrigger: stOpts
      }
    );

    if (tween.scrollTrigger && stOpts.onUpdate) {
      stOpts.onUpdate(tween.scrollTrigger);
    }

    return function destroy() {
      var stTr = tween.scrollTrigger;
      tween.kill();
      if (stTr) stTr.kill();
      img.style.transform = "";
      img.style.transformOrigin = "";
      img.style.opacity = "";
      img.style.transition = "";
    };
  }

  function destroyScaleOnly(root) {
    if (!root) return;
    if (typeof root._imageBannerScaleDestroy === "function") {
      root._imageBannerScaleDestroy();
      root._imageBannerScaleDestroy = null;
    }
    root._ibAlignStart = null;
    root._ibRangePx = null;
    root.removeAttribute("data-image-banner-scale-init");
  }

  function initRoot(root) {
    if (!root) return;

    var img = root.querySelector(".simple-image-banner__image");
    if (!img) return;

    var maxScale = parseScaleMax(root);
    if (prefersReduced || maxScale <= 1) return;

    destroyScaleOnly(root);
    root.setAttribute("data-image-banner-scale-init", "true");

    var destroy;
    if (typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined") {
      try {
        destroy = initGsap(root, img, maxScale);
      } catch (e) {
        destroy = initNative(root, img, maxScale);
      }
    } else {
      destroy = initNative(root, img, maxScale);
    }

    root._imageBannerScaleDestroy = destroy;
  }

  function scan() {
    document.querySelectorAll("section[data-sticky-active='true']").forEach(
      function (root) {
        if (root.querySelector(".simple-image-banner__fixed")) {
          initAlignToMainContent(root);
        }
      }
    );
    document.querySelectorAll("[data-image-banner-scroll-scale]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scan);
  } else {
    scan();
  }

  window.addEventListener("load", function () {
    if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
    document.querySelectorAll("section[data-sticky-active='true']").forEach(
      function (root) {
        if (typeof root._imageBannerMainSync === "function") {
          root._imageBannerMainSync();
        } else if (root.querySelector(".simple-image-banner__fixed")) {
          initAlignToMainContent(root);
        }
      }
    );
  });

  document.addEventListener("shopify:section:load", function (e) {
    var target = getShopifySectionRootFromEventTarget(e.target);
    if (!target || !target.querySelector) return;
    var alignRoot = target.querySelector("section[data-sticky-active='true']");
    if (alignRoot && alignRoot.querySelector(".simple-image-banner__fixed")) {
      initAlignToMainContent(alignRoot);
    }
    var root = target.querySelector("[data-image-banner-scroll-scale]");
    if (root) initRoot(root);
    if (typeof ScrollTrigger !== "undefined") ScrollTrigger.refresh();
  });

  document.addEventListener("shopify:section:unload", function (e) {
    var target = getShopifySectionRootFromEventTarget(e.target);
    if (!target || !target.querySelector) return;
    var alignRoot = target.querySelector("section[data-sticky-active='true']");
    if (alignRoot) destroyAlignForSection(alignRoot);
    var root = target.querySelector("[data-image-banner-scroll-scale]");
    if (root) destroyScaleOnly(root);
  });
})();
